import { Globe, Languages, Heart } from 'lucide-react';
import { cvData } from '../../data/cv';
import { useInView } from '../../hooks/useInView';
import { CountUp } from '../effects/CountUp';
import { TiltCard } from '../effects/TiltCard';

const StatCard = ({ value, suffix, label, delay, animated = true }: { value: number | string; suffix?: string; label: string; delay: string; animated?: boolean }) => {
  const { ref, inView } = useInView();
  return (
    <TiltCard className={`rounded-2xl ${inView ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-8'} transition-all duration-500`}>
      <div
        ref={ref}
        className="group relative gradient-card rounded-2xl p-5 sm:p-6 md:p-7 text-center shadow-card border border-primary/8 hover:shadow-elevated transition-all duration-500 overflow-hidden h-full"
        style={{ transitionDelay: delay }}
      >
        <div className="absolute inset-0 bg-gradient-to-br from-primary/5 via-transparent to-accent/5 opacity-0 group-hover:opacity-100 transition-opacity duration-500" />
        <p className="text-3xl sm:text-4xl md:text-5xl font-bold text-gradient mb-2 relative leading-none" style={{ fontFamily: "'Playfair Display', serif" }}>
          {animated && typeof value === 'number' ? <CountUp to={value} suffix={suffix ?? ''} /> : <>{value}{suffix ?? ''}</>}
        </p>
        <p className="text-[10px] sm:text-xs text-muted-foreground font-semibold uppercase tracking-[0.15em] relative">{label}</p>
      </div>
    </TiltCard>
  );
};

export const About = () => {
  const { ref: headRef, inView: headIn } = useInView();
  const { ref: leftRef, inView: leftIn } = useInView();
  const { ref: rightRef, inView: rightIn } = useInView();

  const infoItems = [
    { icon: Globe, label: 'Location', value: cvData.personal.location },
    { icon: Languages, label: 'Languages', value: cvData.languages.join(', ') },
    { icon: Heart, label: 'Interests', value: cvData.hobbies.slice(0, 5).join(', ') + '...' },
  ];

  return (
    <section id="about" className="py-20 sm:py-24 md:py-28 px-4 sm:px-6 md:px-12 lg:px-20 bg-card relative">
      <div className="max-w-7xl mx-auto">

        {/* Asymmetric header 40/60 */}
        <div ref={headRef} className={`grid md:grid-cols-10 gap-8 md:gap-16 lg:gap-20 items-start mb-12 sm:mb-16 md:mb-20 transition-all duration-700 ${headIn ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-8'}`}>
          <div className="md:col-span-4">
            <p className="text-[10px] font-bold uppercase tracking-[0.3em] text-accent mb-3 md:mb-4">Who I Am</p>
            <h2
              className="text-3xl sm:text-4xl md:text-5xl lg:text-6xl font-bold leading-[1.05] text-foreground"
              style={{ fontFamily: "'Playfair Display', serif" }}
            >
              A Vision for <br /><span className="text-accent italic font-normal">Primary Care</span>
            </h2>
            <div className="mt-5 md:mt-6 w-16 h-1 bg-accent rounded-full" />
          </div>
          <div className="md:col-span-6 border-l-2 border-accent/30 pl-5 sm:pl-6 md:pl-12">
            {cvData.about.split('\n').map((paragraph, idx) => (
              <p key={idx} className="text-base sm:text-lg md:text-xl font-light leading-relaxed text-foreground/75 mb-5 md:mb-6 last:mb-0">
                {paragraph}
              </p>
            ))}
          </div>
        </div>

        {/* Info grid */}
        <div ref={leftRef} className={`grid sm:grid-cols-2 md:grid-cols-3 gap-5 sm:gap-6 md:gap-8 mb-10 md:mb-14 transition-all duration-700 delay-100 ${leftIn ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-6'}`}>
          {infoItems.map(({ icon: Icon, label, value }) => (
            <div key={label} className="flex items-start gap-4 group p-5 md:p-6 bg-background rounded-xl border border-border hover:border-accent/40 hover:shadow-card transition-all duration-300">
              <div className="w-11 h-11 rounded-xl bg-primary/8 flex items-center justify-center flex-shrink-0 group-hover:bg-accent/15 transition-colors">
                <Icon size={18} className="text-primary group-hover:text-accent transition-colors" />
              </div>
              <div className="min-w-0">
                <p className="text-[10px] font-bold uppercase tracking-[0.2em] text-accent mb-1">{label}</p>
                <p className="text-sm text-foreground/80 break-words">{value}</p>
              </div>
            </div>
          ))}
        </div>

        <div ref={rightRef} className={`grid grid-cols-2 md:grid-cols-4 gap-4 sm:gap-5 transition-all duration-700 delay-200 ${rightIn ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-6'}`}>
          {[
            { value: 3, suffix: '+', label: 'Years in UK Healthcare', delay: '0ms', animated: true },
            { value: 'M.D.', label: 'Medical Degree', delay: '100ms', animated: false },
            { value: 5, suffix: '+', label: 'Roles & Experiences', delay: '200ms', animated: true },
            { value: 22, suffix: '+', label: 'Courses & Certifications', delay: '300ms', animated: true },
          ].map((stat) => (
            <StatCard key={stat.label} {...stat} />
          ))}
        </div>
      </div>
    </section>

  );
};
